/**
 * RoutingPolicy editor (ADR-126 Decision 4).
 *
 * Lists the per-tenant `(when_state → adjustment)` routing policies governing
 * the selected scope and lets an admin add/edit/remove them. For a site scope
 * it shows BOTH the site's own policies and the inherited org defaults (the
 * inherited rows are read-only, flagged "Inherited"); for the org scope it
 * edits the org-tier defaults directly.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  EmptyState,
  StatusBadge,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import type { ConfigScope, ScopeRow } from './types';
import { scopeOf, ownKeysFrom } from './types';
import { useScopeRows } from './useScopeRows';
import { ScopeGroupTable } from './ScopeGroupTable';
import {
  useRoutingPolicies,
  useRoutingPoliciesForSchool,
  useDeleteRoutingPolicy,
} from '../../hooks/queries';
import {
  OPERATING_CONDITION_LABELS,
  ROUTING_ADJUSTMENT_LABELS,
  WHEN_EVENT_LABELS,
  type RoutingPolicy,
} from '../../services';
import { RoutingPolicyDrawer } from './drawers/RoutingPolicyDrawer';
import { toaster } from '../ui/toaster';

/**
 * Routing tab: a site-grouped table — "Organization default" + one
 * row per site — each expanding to that scope's routing policies, rather than
 * gating behind a scope-selector dropdown. The org default and each site are
 * edited independently.
 */
export function RoutingPolicyEditor() {
  const { t } = useTranslation();
  const { data: policies = [], isLoading: policiesLoading } =
    useRoutingPolicies({});
  const ownKeys = useMemo(() => ownKeysFrom(policies), [policies]);
  const { rows, isLoading: rowsLoading, error } = useScopeRows(ownKeys);

  const countFor = (row: ScopeRow) =>
    policies.filter((p) =>
      row.kind === 'org'
        ? p.school == null && p.district === row.districtId
        : p.school === row.schoolId,
    ).length;

  return (
    <ScopeGroupTable
      rows={rows}
      isLoading={policiesLoading || rowsLoading}
      error={error}
      searchPlaceholder={t('whakahaere.searchSites', 'Search sites')}
      summaryColumn={{
        key: 'policies',
        label: t('whakahaere.columns.policies', 'Policies'),
        sortable: false,
        width: '110px',
        render: (row) => (
          <Text fontSize="sm" color="text.secondary">
            {countFor(row)}
          </Text>
        ),
      }}
      renderExpandedRow={(row) => <RoutingPolicyPanel scope={scopeOf(row)} />}
      e2eTitle="routing-scopes"
    />
  );
}

interface RoutingPolicyPanelProps {
  scope: ConfigScope;
}

/** A policy row decorated with whether it is inherited (read-only) at this scope. */
interface DecoratedPolicy extends RoutingPolicy {
  __inherited: boolean;
}

function RoutingPolicyPanel({ scope }: RoutingPolicyPanelProps) {
  const { t } = useTranslation();
  // Org scope: org-tier rows only. Site scope: own + inherited org defaults.
  const orgQuery = useRoutingPolicies(
    scope.schoolId ? {} : { district: scope.districtId },
  );
  const siteQuery = useRoutingPoliciesForSchool(
    scope.schoolId ?? undefined,
  );
  const deletePolicy = useDeleteRoutingPolicy();

  const [edit, setEdit] = useState<{ open: boolean; row: RoutingPolicy | null }>(
    { open: false, row: null },
  );

  const isLoading = scope.schoolId ? siteQuery.isLoading : orgQuery.isLoading;
  const error = scope.schoolId ? siteQuery.error : orgQuery.error;

  const rows = useMemo<DecoratedPolicy[]>(() => {
    if (scope.schoolId) {
      return (siteQuery.data ?? []).map((p) => ({
        ...p,
        // A row attached at the org tier is inherited from the site's POV.
        __inherited: p.school == null,
      }));
    }
    // Routing-policy list is unpaginated (bare array, not a `{results}`
    // envelope) — see routingPolicy.service.
    return (orgQuery.data ?? []).map((p) => ({
      ...p,
      __inherited: false,
    }));
  }, [scope.schoolId, siteQuery.data, orgQuery.data]);

  const handleDelete = (id: number) => {
    deletePolicy.mutate(id, {
      onSuccess: () => toaster.create({ title: t('whakahaere.policyRemoved', 'Policy removed'), type: 'success' }),
      onError: (err) =>
        toaster.create({
          title: t('whakahaere.couldNotRemovePolicy', 'Could not remove the policy'),
          description: err instanceof Error ? err.message : undefined,
          type: 'error',
        }),
    });
    setEdit({ open: false, row: null });
  };

  const columns: Column<DecoratedPolicy>[] = [
    {
      key: 'name',
      label: t('whakahaere.columns.name', 'Name'),
      render: (row) => (
        <Text>
          {row.name ||
            `${OPERATING_CONDITION_LABELS[row.when_state ?? 'not_operating']} → ${ROUTING_ADJUSTMENT_LABELS[row.adjustment ?? 'restrict_to_on_duty']}`}
        </Text>
      ),
    },
    {
      key: 'when_state',
      label: t('whakahaere.columns.when', 'When'),
      render: (row) => {
        const whenEvent = row.when_event ?? 'any';
        return (
          <Box>
            <Text>
              {OPERATING_CONDITION_LABELS[row.when_state ?? 'not_operating']}
            </Text>
            {/* Only surface the scheduled-event predicate when it narrows the
                policy — `any` is the no-op default. */}
            {whenEvent !== 'any' && (
              <Text fontSize="sm" color="text.secondary">
                {WHEN_EVENT_LABELS[whenEvent]}
              </Text>
            )}
          </Box>
        );
      },
    },
    {
      key: 'adjustment',
      label: t('whakahaere.columns.adjustment', 'Adjustment'),
      render: (row) => (
        <Text>{ROUTING_ADJUSTMENT_LABELS[row.adjustment ?? 'restrict_to_on_duty']}</Text>
      ),
    },
    {
      key: 'alert_type',
      label: t('whakahaere.columns.alertType', 'Alert type'),
      render: (row) => (
        <Text color={row.alert_type_name ? undefined : 'text.secondary'}>
          {row.alert_type_name || t('whakahaere.allAlerts', 'All alerts')}
        </Text>
      ),
    },
    {
      key: 'priority',
      label: t('whakahaere.columns.priority', 'Priority'),
      render: (row) => <Text>{row.priority}</Text>,
    },
    {
      key: 'status',
      label: t('whakahaere.columns.status', 'Status'),
      render: (row) => {
        if (row.__inherited) {
          return <StatusBadge status={'info' as StatusType} label={t('whakahaere.statusInherited', 'Inherited')} />;
        }
        return (
          <StatusBadge
            status={(row.is_active ? 'active' : 'inactive') as StatusType}
            label={row.is_active ? t('whakahaere.statusActive', 'Active') : t('whakahaere.statusInactive', 'Inactive')}
          />
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : t('whakahaere.failedToLoadPolicies', 'Failed to load policies')}
        </Text>
      </Center>
    );
  }

  return (
    <Box px={5} py={5}>
      <Flex justify="space-between" align="center" mb={4}>
        <Box>
          <Text fontWeight="600">{t('whakahaere.routingPolicies', 'Routing policies')}</Text>
          <Text fontSize="sm" color="text.secondary">
            {t('whakahaere.routingPoliciesDesc', 'When the site\'s operating state matches, narrow who an alert pages. Site policies override the inherited organization defaults.')}
          </Text>
        </Box>
        <ButtonComponent
          visual="primary"
          onClick={() => setEdit({ open: true, row: null })}
          data-e2e="add-routing-policy-button"
        >
          {t('whakahaere.addPolicy', 'Add policy')}
        </ButtonComponent>
      </Flex>

      {rows.length === 0 ? (
        <EmptyState
          icon={HiAdjustmentsHorizontal}
          title={t('whakahaere.noRoutingPolicies', 'No routing policies')}
          description={t('whakahaere.noRoutingPoliciesDesc', 'Add a policy to shrink fan-out (e.g. on-duty only) when this site is not operating.')}
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          keyExtractor={(row) => String(row.id)}
          onRowClick={(row) => {
            // Inherited org rows are read-only from a site scope.
            if (row.__inherited) return;
            setEdit({ open: true, row });
          }}
        />
      )}

      <RoutingPolicyDrawer
        scope={scope}
        isOpen={edit.open}
        row={edit.row}
        onClose={() => setEdit({ open: false, row: null })}
        onDelete={handleDelete}
      />
    </Box>
  );
}
